/* What the DreamCloud dashboard shows, and what a replay ticks by default.
 *
 * Pure, so the arithmetic behind every tile and the rule for "which sales get
 * re-sent unless the owner says otherwise" can be tested without a worker. The
 * response shapes are the DreamCloud worker's (worker/src/dashboard.js); a
 * status or field this does not know is shown as it came, never guessed at.
 */

function num(v) {
  var n = Number(v);
  return v == null || v === '' || !isFinite(n) ? 0 : n;
}

/* This period against the one before it.
 *
 * `pct` is null when the prior is zero: a percentage of nothing is not a
 * number, and "+Infinity%" on a quiet week would be worse than saying nothing.
 * The direction is still reported, since 0 -> 41 did go up.
 */
function compare(current, prior) {
  if (current == null || prior == null) return { delta: null, pct: null, direction: null };
  var delta = current - prior;
  return {
    delta: delta,
    pct: prior > 0 ? Math.round((delta / prior) * 100) : null,
    direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat'
  };
}

/* A prior that is missing is unknown, not zero: comparing against 0 would call
 * every figure an increase. */
function prev(v) { return v == null ? null : num(v); }

function percent(p) { return p == null ? '—' : Math.round(p) + '%'; }

/* Google Ads' own click count over the range. It is a separate feed from the
 * worker's tracked clicks and the two are shown side by side on purpose. Null
 * when the feed has nothing, so the tile says nothing rather than "0". */
function googleAdsClicks(rows) {
  if (!rows || !rows.length) return null;
  var sum = 0;
  for (var i = 0; i < rows.length; i++) sum += num(rows[i].clicks);
  return sum;
}

function tiles(summary) {
  if (!summary || !summary.totals) return [];
  var t = summary.totals;
  var p = summary.prior || {};
  var out = [];

  var ads = googleAdsClicks(summary.google_ads);
  var adsSub = null;
  if (ads != null) {
    // Today's figure is still moving: Google reports it late.
    adsSub = 'Google Ads: ' + ads + (summary.range === 'today' ? ' (provisional)' : '');
  }
  out.push({
    id: 'paid_clicks', label: 'Paid clicks', value: num(t.paid_clicks_google), sub: adsSub,
    compare: compare(num(t.paid_clicks_google), prev(p.paid_clicks_google))
  });
  if (summary.meta_enabled) {
    out.push({
      id: 'paid_clicks_meta', label: 'Meta paid clicks', value: num(t.paid_clicks_meta), sub: null,
      compare: compare(num(t.paid_clicks_meta), prev(p.paid_clicks_meta))
    });
  }
  out.push({
    id: 'online_bookings', label: 'Online bookings', value: num(t.online_bookings), sub: null,
    compare: compare(num(t.online_bookings), prev(p.online_bookings))
  });
  out.push({
    id: 'coverage', label: 'Click coverage', value: percent(t.coverage_pct),
    sub: num(t.online_bookings_with_click) + ' of ' + num(t.online_bookings) + ' bookings had a click',
    compare: compare(t.coverage_pct == null ? null : t.coverage_pct, p.coverage_pct == null ? null : p.coverage_pct)
  });
  out.push({
    id: 'conversions', label: 'Conversions accepted', value: num(t.uploads_accepted),
    sub: num(t.uploads_failed) + ' failed · ' + num(t.uploads_pending) + ' pending',
    compare: compare(num(t.uploads_accepted), prev(p.uploads_accepted))
  });
  var m = num(t.mobile_clicks);
  var d = num(t.desktop_clicks);
  var mobile = m + d > 0 ? Math.round((100 * m) / (m + d)) : null;
  out.push({
    id: 'mobile', label: 'Mobile / desktop', sub: null,
    value: mobile == null ? '—' : mobile + '% / ' + (100 - mobile) + '%',
    compare: compare(t.mobile_pct == null ? null : t.mobile_pct, p.mobile_pct == null ? null : p.mobile_pct)
  });
  return out;
}

/* Fatal first, then the newest. A copy, so the list the store holds is not
 * reordered under a component that is reading it. */
function orderProblems(problems) {
  return (problems || []).slice().sort(function (a, b) {
    var fa = a.severity === 'fatal' ? 0 : 1;
    var fb = b.severity === 'fatal' ? 0 : 1;
    if (fa !== fb) return fa - fb;
    return String(b.first_seen).localeCompare(String(a.first_seen));
  });
}

/* The sales a replay ticks before the owner touches anything.
 *
 * Only the ones that evidently did not land: never sent, the send failed, or
 * Google refused them. `pending` may still be on its way, `accepted` is done,
 * `rejected_batch` and the `unknown_*` ones need a person to look first, and an
 * ineligible sale has no click to send. Re-sending a sale that did land costs
 * a duplicate conversion, so every doubtful case is left unticked.
 */
var REPLAYABLE = { never_sent: true, send_failed: true, rejected: true };

function defaultTicks(rows) {
  var ticks = new Set();
  (rows || []).forEach(function (r) {
    if (r && r.eligible && REPLAYABLE[r.status] === true) ticks.add(r.sale_id);
  });
  return ticks;
}

var LABELS = {
  never_sent: 'Never sent',
  send_failed: 'Send failed',
  rejected: 'Rejected by Google',
  rejected_batch: 'Rejected (batch) — check',
  pending: 'Pending',
  accepted: 'Accepted',
  unknown_replayed_before_tracking: 'Unknown: replayed before tracking',
  unknown_log_expired: 'Unknown: log expired'
};

/* A status the worker adds later shows as its own id: a visible raw word is
 * better than a wrong label or a blank. */
function statusLabel(status) {
  return Object.prototype.hasOwnProperty.call(LABELS, status) ? LABELS[status] : String(status);
}

function fmt(n) { return String(Math.round(n * 10) / 10); }

/* SVG `points` for each series, on one shared y-scale from 0 to the largest
 * value in any of them (so lines are comparable). A max of 0 keeps every line
 * flat on the bottom edge rather than dividing by it. */
function chartPaths(days, series, width, height) {
  var rows = days || [];
  var max = 0;
  rows.forEach(function (r) {
    series.forEach(function (s) { max = Math.max(max, num(r[s])); });
  });
  var step = rows.length > 1 ? width / (rows.length - 1) : 0;
  var out = {};
  series.forEach(function (s) {
    out[s] = rows.map(function (r, i) {
      var y = max > 0 ? height - (num(r[s]) / max) * height : height;
      return fmt(i * step) + ',' + fmt(y);
    }).join(' ');
  });
  return out;
}

/* Says nothing while the data is fresh; the poll is every minute, so two
 * minutes without an update means a poll has failed. */
function staleness(lastOkAt, now) {
  if (!lastOkAt) return null;
  var mins = Math.floor((now.getTime() - lastOkAt.getTime()) / 60000);
  if (mins < 2) return null;
  if (mins < 60) return 'Last updated ' + mins + ' min ago';
  return 'Last updated ' + Math.floor(mins / 60) + ' h ago';
}

/* The chart's rows: each day with the third series added, the online bookings
 * that arrived without a click. Never negative: a day whose with-click count
 * ran ahead (a late-matched click) draws at zero rather than below the axis. */
function chartDays(days) {
  return (days || []).map(function (d) {
    return Object.assign({}, d, {
      online_bookings_no_click: Math.max(0, num(d.online_bookings) - num(d.online_bookings_with_click))
    });
  });
}

/* The top of the chart's shared y-scale: the same max chartPaths draws to. */
function seriesMax(days, series) {
  var max = 0;
  (days || []).forEach(function (r) {
    series.forEach(function (s) { max = Math.max(max, num(r[s])); });
  });
  return max;
}

/* A tile's comparison as words, and whether the change is good news.
 *
 * Coverage and mobile share are percentages already, so their change is in
 * points: "a 12% fall in a percentage" reads as something else. A prior of zero
 * has no percentage (see compare), so the plain difference is shown instead.
 * Mobile share going up or down is neither good nor bad, so it is neutral. */
var POINTS = { coverage: true, mobile: true };

function compareText(tile, range) {
  var c = tile && tile.compare;
  if (!c || c.direction == null) return null;
  var against = range === 'today' ? 'vs yesterday' : 'vs prior ' + range + ' days';
  if (c.direction === 'flat') return { text: 'Same as ' + against.slice(3), tone: 'flat' };
  var arrow = c.direction === 'up' ? '▲ ' : '▼ ';
  var size;
  if (POINTS[tile.id]) size = Math.abs(Math.round(c.delta)) + ' pts';
  else if (c.pct != null) size = Math.abs(c.pct) + '%';
  else size = Math.abs(c.delta);
  var tone = tile.id === 'mobile' ? 'flat' : c.direction === 'up' ? 'good' : 'bad';
  return { text: arrow + size + ' ' + against, tone: tone };
}

/* What a problem's button opens.
 *
 *   coverage_dropped  the online bookings without a click
 *   sales_not_sent    the replay dialog, over the dates the worker recorded
 *   job_missing:<job> that job's run history
 *   webhook_failures  the webhook failures (they carry their own signatures)
 *   anything else     the failures recorded under its signature
 */
function problemAction(p) {
  var sig = String((p && p.signature) || '');
  if (sig === 'coverage_dropped') {
    return { type: 'bookings', label: 'See bookings', params: { has_click: false } };
  }
  if (sig === 'sales_not_sent') {
    return { type: 'replay', label: 'Replay…', start: p.replay_start || null, end: p.replay_end || null };
  }
  if (sig.indexOf('job_missing:') === 0) {
    return { type: 'job', label: 'See runs', job: sig.slice('job_missing:'.length) };
  }
  if (sig === 'webhook_failures') {
    return { type: 'failures', label: 'See failures', params: { area: 'webhook' } };
  }
  return { type: 'failures', label: 'See failures', params: { signature: sig } };
}

/* A job's most recent run, scheduled or by hand, whichever started later. */
function lastRun(entry) {
  if (!entry) return null;
  var a = entry.latest || null;
  var b = entry.latest_manual || null;
  if (!a || !b) return a || b;
  return String(b.started_at) > String(a.started_at) ? b : a;
}

/* The dot: ok, failed, skipped, dry_run, running (no outcome yet) or none. */
function runState(run) {
  if (!run) return 'none';
  if (!run.outcome) return 'running';
  return run.outcome;
}

function jobName(id) {
  var s = String(id || '').replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* One line for a run: the error when it failed, otherwise the summary's plain
 * values ("added 3 · removed 1"). Nested objects are left out: this is a line,
 * and the history has the rest. */
function runSummary(run) {
  if (!run) return 'No run on record';
  if (run.outcome === 'failed') return 'Failed: ' + (run.error || 'no error recorded');
  var sum = run.summary;
  if (sum == null) return run.outcome === 'skipped' ? 'Skipped' : (run.outcome ? '' : 'Running');
  if (typeof sum !== 'object') return String(sum);
  var parts = [];
  Object.keys(sum).forEach(function (k) {
    var v = sum[k];
    if (v == null || typeof v === 'object') return;
    parts.push(k.replace(/_/g, ' ') + ' ' + v);
  });
  var line = parts.slice(0, 4).join(' · ');
  return run.outcome === 'skipped' ? 'Skipped' + (line ? ': ' + line : '') : line;
}

/* A timestamp as the page shows it, in the browser's own time zone (the
 * console runs in the salon): the time alone for today, the weekday and time
 * within the week, the date and time before that. A missing or unreadable one
 * is a dash, never "Invalid Date". */
function formatWhen(iso, now) {
  if (!iso) return '—';
  var d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  var n = now || new Date();
  var time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === n.toDateString()) return time;
  var ageDays = (n.getTime() - d.getTime()) / 86400000;
  if (ageDays >= 0 && ageDays < 6) return d.toLocaleDateString(undefined, { weekday: 'short' }) + ' ' + time;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + time;
}

/* ── Actions ─────────────────────────────────────────────────────────────────
 *
 * Every write the page can make is a dry run, a review, then a confirm. The two
 * that carry the most weight, replaying conversions and storing a call report,
 * are state machines here so the rule "the confirm sends what the dry run
 * looked at" is a tested function, not something a component remembers to do.
 */

var DAY_MS = 86400000;
var MAX_RANGE_DAYS = 31;           // the worker's dashboard MAX_RANGE_DAYS
var REPLAY_BATCH = 200;            // the worker's REPLAY_MAX_SALES
var MAX_CSV_BYTES = 2 * 1024 * 1024;

var DESTINATIONS = ['google_ads', 'ga4', 'google_ads_booking'];
var DEST_LABELS = {
  google_ads: 'Google Ads sale',
  ga4: 'GA4 purchase',
  google_ads_booking: 'Appointment Booked restatement'
};

function plainDay(v) {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  var t = Date.parse(v + 'T00:00:00Z');
  return isFinite(t) && new Date(t).toISOString().slice(0, 10) === v;
}

/* Why a replay range cannot be listed, or null. The worker refuses the same
 * things; saying so in the form saves the round trip. */
function rangeError(start, end) {
  if (!plainDay(start) || !plainDay(end)) return 'Choose both dates.';
  if (end < start) return 'The end date is before the start.';
  var days = Math.round((Date.parse(end + 'T00:00:00Z') - Date.parse(start + 'T00:00:00Z')) / DAY_MS) + 1;
  if (days > MAX_RANGE_DAYS) return 'At most ' + MAX_RANGE_DAYS + ' days at a time.';
  return null;
}

function pad(n) { return (n < 10 ? '0' : '') + n; }

/* The day before `now`, in the browser's own time zone (the salon's). */
function yesterday(now) {
  var d = new Date((now || new Date()).getTime());
  d.setDate(d.getDate() - 1);
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

function money(v) {
  if (v == null || v === '' || !isFinite(Number(v))) return '—';
  return '$' + Number(v).toFixed(2);
}

function replayBatches(ids) {
  var out = [];
  for (var i = 0; i < (ids || []).length; i += REPLAY_BATCH) out.push(ids.slice(i, i + REPLAY_BATCH));
  return out;
}

function sameSet(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  var s = new Set(a);
  for (var i = 0; i < b.length; i++) if (!s.has(b[i])) return false;
  return true;
}

function byNumber(a, b) { return a - b; }

/* The replay dialog's state.
 *
 *   pick       dates chosen, nothing listed yet
 *   listed     the sales are on screen with their ticks; a dry run is needed
 *   previewed  the dry run answered for exactly `previewIds` / `previewDestinations`
 *   sending    the confirm is going out, in `sending.batches`
 *   done | failed
 *
 * Changing a tick or a destination after the dry run goes back to `listed`, so
 * the confirm can only ever send what a dry run showed. A list that reloads
 * while `previewed` (the re-list after a send, a second click) replaces the
 * rows but not the ticks or the previewed ids: new sales are not slipped in.
 */
function initialReplay(start, end) {
  return {
    step: 'pick', start: start || null, end: end || null,
    rows: [], ticks: [], destinations: ['google_ads'],
    previewIds: null, previewDestinations: null, preview: null,
    sending: null, sent: null, error: null,
    // A send ended without knowing whether its last batch landed. Until the
    // dates change or another send starts, a list pre-ticks nothing.
    unknownSend: false
  };
}

function cleared(s) {
  return Object.assign(s, { step: 'listed', previewIds: null, previewDestinations: null, preview: null });
}

function sentTotals(responses) {
  var t = { batches: 0, sales: 0, google_ads: 0, ga4: 0 };
  (responses || []).forEach(function (r) {
    t.batches++;
    t.sales += num(r && r.sales);
    var ing = (r && r.ingest) || {};
    t.google_ads += num(ing.google_ads_events);
    t.ga4 += num(ing.ga4_events);
  });
  return t;
}

/* The conversions the confirm would send: the dry run's would_send, over the
 * destinations it was run for. */
function replayCount(state) {
  if (!state || state.step !== 'previewed' || !state.preview) return 0;
  var w = state.preview.would_send || {};
  return (state.previewDestinations || []).reduce(function (n, d) { return n + num(w[d]); }, 0);
}

function replayStep(state, event) {
  var s = Object.assign({}, state || initialReplay());
  var e = event || {};
  var step = s.step;

  switch (e.type) {
    case 'range':
      if (step === 'sending') return state;
      return Object.assign(initialReplay(e.start, e.end), { destinations: s.destinations.slice() });

    case 'listed': {
      if (step === 'sending') return state;
      s.rows = Array.isArray(e.rows) ? e.rows.slice() : [];
      s.error = null;
      if (step === 'previewed') return s;
      // After a send whose result is unknown, a sale that evidently did not
      // land may simply not show it yet; re-sending it on a pre-tick would
      // send it twice. The owner ticks deliberately instead.
      s.ticks = s.unknownSend ? [] : Array.from(defaultTicks(s.rows)).sort(byNumber);
      return cleared(s);
    }

    case 'tick': {
      if (step !== 'listed' && step !== 'previewed') return state;
      var known = s.rows.some(function (r) { return r.sale_id === e.id; });
      if (!known) return state;
      var ticks = s.ticks.filter(function (id) { return id !== e.id; });
      if (e.on) ticks.push(e.id);
      s.ticks = ticks.sort(byNumber);
      return cleared(s);
    }

    case 'destination': {
      if (step === 'sending' || e.id === 'google_ads' || DESTINATIONS.indexOf(e.id) < 0) return state;
      var on = s.destinations.filter(function (d) { return d !== e.id; });
      if (e.on) on.push(e.id);
      s.destinations = DESTINATIONS.filter(function (d) { return on.indexOf(d) >= 0; });
      if (step === 'previewed') return cleared(s);
      return s;
    }

    case 'previewed':
      // An answer for ticks or destinations that have since changed is stale.
      if (step !== 'listed') return state;
      if (!sameSet(e.ids, s.ticks) || !sameSet(e.destinations, s.destinations)) return state;
      return Object.assign(s, {
        step: 'previewed', previewIds: e.ids.slice(), previewDestinations: e.destinations.slice(),
        preview: e.response || null, error: null
      });

    case 'confirm':
      if (step !== 'previewed' || replayCount(s) <= 0) return state;
      return Object.assign(s, {
        step: 'sending', error: null, unknownSend: false,
        sending: { batches: replayBatches(s.previewIds), destinations: s.previewDestinations.slice() }
      });

    case 'sent':
      if (step !== 'sending') return state;
      // GA4 and the restatement are opt-in for each send, not remembered.
      return Object.assign(s, { step: 'done', sent: sentTotals(e.responses), error: null, destinations: ['google_ads'] });

    case 'failed':
      if (step !== 'sending') return state;
      return Object.assign(s, {
        step: 'failed', sent: sentTotals(e.responses), error: e.error || 'That did not work.', destinations: ['google_ads'],
        // Only the worker's own word that Google refused the batch makes it known.
        unknownSend: e.unknown !== false
      });

    case 'error':
      if (step === 'sending') return state;
      return Object.assign(s, { error: e.error || 'That did not work.' });

    default:
      return state;
  }
}

/* A dry run over more than 200 ticked sales goes out in batches like the send
 * does; this puts the answers back together as one preview. */
function mergePreviews(responses) {
  var out = { ok: true, dry_run: true, sales: 0, results: [], would_send: { google_ads: 0, ga4: 0, google_ads_booking: 0 } };
  (responses || []).forEach(function (r) {
    if (!r) return;
    out.sales += num(r.sales);
    out.results = out.results.concat(Array.isArray(r.results) ? r.results : []);
    DESTINATIONS.forEach(function (d) { out.would_send[d] += num(r.would_send && r.would_send[d]); });
  });
  return out;
}

function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

function sentParts(t) {
  var s = plural(num(t && t.sales), 'sale', 'sales') + ': ' + plural(num(t && t.google_ads), 'Google Ads event', 'Google Ads events');
  if (num(t && t.ga4)) s += ', ' + plural(num(t.ga4), 'GA4 event', 'GA4 events');
  return s;
}

/* What a finished send says. */
function replaySentText(totals) { return 'Sent ' + sentParts(totals) + '.'; }

/* What a send that stopped says, claiming no more than the page knows.
 *
 *   batch, batches  the batch that stopped it (1-based) and how many there were
 *   unknown         true when the call threw (network, timeout, 5xx): the
 *                   worker may have sent it. false when the worker answered
 *                   that Google refused it (ingest.status 'error'): nothing in
 *                   it was sent.
 *   sent            sentTotals of the batches that answered cleanly before it
 */
function replayFailureText(f) {
  var parts = [];
  if (f.unknown) {
    parts.push('Batch ' + f.batch + ' of ' + f.batches + ' did not answer cleanly (' + f.error + ').');
    parts.push('Its result is unknown: check the statuses below before replaying again.');
  } else {
    parts.push('Google did not take batch ' + f.batch + ' of ' + f.batches + ' (' + f.error + '); nothing in it was sent.');
  }
  if (f.sent && num(f.sent.batches) > 0) parts.push('Known sent before it: ' + sentParts(f.sent) + '.');
  var rest = f.batches - f.batch;
  if (rest === 1) parts.push('Batch ' + f.batches + ' was not sent.');
  else if (rest > 1) parts.push('Batches ' + (f.batch + 1) + '–' + f.batches + ' were not sent.');
  return parts.join(' ');
}

/* A failed call as one sentence: the function's own words, then the worker's
 * `detail` when its answer had one ("ingest failed: insert calls (500)"). */
function errorText(err) {
  if (!err) return 'That did not work.';
  var errs = err.body && Array.isArray(err.body.errors) ? err.body.errors.length : 0;
  if (!err.said && errs) {
    return 'DreamCloud reported ' + plural(errs, 'error', 'errors') + (err.status ? ' (HTTP ' + err.status + ')' : '');
  }
  var s = err.said || err.message || 'That did not work.';
  var d = err.body && err.body.detail;
  return typeof d === 'string' && d ? s + ': ' + d : s;
}

/* A More-actions Apply that threw, as words. Whatever the error, the write may
 * have done part of its work before it failed (audience sync answers 502 with
 * its counts), so it always says so and points at the dry run, which shows
 * what is left. The edge function's sentence often ends in its own full stop;
 * it is not doubled. With { counts: true } it adds what the failed write
 * reported doing (for the one-line outcome; the panel lists them itself). */
var PARTLY_APPLIED = 'It may have been partly applied; run the dry run again to see what’s left.';
var FAILED_COUNTS_SKIP = { Error: true, Detail: true };

function applyFailureText(err, opts) {
  var s = errorText(err).replace(/\s+$/, '');
  if (!/[.!?…]$/.test(s)) s += '.';
  var out = s + ' ' + PARTLY_APPLIED;
  if (opts && opts.counts && err && err.body) {
    var c = resultCounts(err.body).filter(function (x) { return !FAILED_COUNTS_SKIP[x.label]; });
    if (c.length) {
      out += ' Reported before it failed: ' + c.map(function (x) { return x.label + ' ' + x.value; }).join(', ') + '.';
    }
  }
  return out;
}

var SKIP_REASONS = {
  no_client: 'no client on the sale',
  consent_declined: 'declined ad tracking',
  not_for_destinations: 'nothing for the ticked destinations',
  no_events: 'nothing to send'
};

/* One dry-run result as words. A reason this does not know shows as it came. */
function previewLabel(r) {
  if (!r) return '';
  if (r.status === 'would_send') {
    return 'Would send: ' + (r.destinations || []).map(function (d) { return DEST_LABELS[d] || d; }).join(', ');
  }
  if (r.status === 'skipped') {
    var why = Object.prototype.hasOwnProperty.call(SKIP_REASONS, r.reason) ? SKIP_REASONS[r.reason] : String(r.reason);
    return 'Skipped: ' + why;
  }
  return String(r.status);
}

/* The call report upload.
 *
 *   pick      a file chosen (or not); a refused file or preview stays here
 *   previewed the worker parsed it: parsed > 0 and no error
 *   storing   the store is going out
 *   done | failed
 *
 * Only `previewed` with new calls can be stored, so a file that is not a call
 * report never reaches a store button. */
function initialCallReport() {
  return { step: 'pick', name: null, csv: null, preview: null, result: null, error: null };
}

function callReportStep(state, event) {
  var s = Object.assign({}, state || initialCallReport());
  var e = event || {};
  switch (e.type) {
    case 'file': {
      if (s.step === 'storing') return state;
      var fresh = Object.assign(initialCallReport(), { name: e.name || null });
      if (num(e.size) > MAX_CSV_BYTES) return Object.assign(fresh, { error: 'That file is too large (max 2 MB).' });
      if (typeof e.csv !== 'string' || !e.csv.trim()) return Object.assign(fresh, { error: 'That file is empty.' });
      return Object.assign(fresh, { csv: e.csv });
    }
    case 'preview': {
      if (s.step !== 'pick' || !s.csv) return state;
      var r = e.response || {};
      if (r.error) return Object.assign(s, { error: String(r.error), preview: null });
      if (!(num(r.parsed) > 0)) return Object.assign(s, { error: 'No calls found in that file.', preview: null });
      return Object.assign(s, { step: 'previewed', preview: r, error: null });
    }
    case 'store':
      if (s.step !== 'previewed' || !(num(s.preview && s.preview.new) > 0)) return state;
      return Object.assign(s, { step: 'storing', error: null });
    case 'stored': {
      if (s.step !== 'storing') return state;
      var got = e.response || {};
      if (got.error) return Object.assign(s, { step: 'failed', error: String(got.error) });
      return Object.assign(s, { step: 'done', result: got, error: null });
    }
    case 'failed':
      if (s.step !== 'storing') return state;
      return Object.assign(s, { step: 'failed', error: e.error || 'That did not work.' });
    case 'reset':
      return initialCallReport();
    default:
      return state;
  }
}

/* A write action's answer as label / value pairs: plain values as they are,
 * yes/no for flags, a list by its size, one level of an object's counts.
 * `samples` (redacted phone fragments from a bad CSV) is never shown, and the
 * envelope fields say nothing an owner needs. */
var RESULT_SKIP = { ok: true, dry_run: true, preview: true, samples: true };

function humanKey(k) {
  var s = String(k).replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function resultCounts(body) {
  if (!body || typeof body !== 'object') return [];
  var out = [];
  Object.keys(body).forEach(function (k) {
    if (RESULT_SKIP[k]) return;
    var v = body[k];
    if (v == null) return;
    if (typeof v === 'boolean') out.push({ label: humanKey(k), value: v ? 'yes' : 'no' });
    else if (typeof v === 'number') out.push({ label: humanKey(k), value: v });
    else if (typeof v === 'string') { if (v.length <= 60) out.push({ label: humanKey(k), value: v }); }
    else if (Array.isArray(v)) out.push({ label: humanKey(k), value: v.length });
    else if (typeof v === 'object') {
      Object.keys(v).forEach(function (sub) {
        var x = v[sub];
        if (typeof x === 'number') out.push({ label: humanKey(k) + ' ' + sub, value: x });
        else if (typeof x === 'boolean') out.push({ label: humanKey(k) + ' ' + sub, value: x ? 'yes' : 'no' });
      });
    }
  });
  return out;
}

export {
  formatWhen,
  compare, tiles, orderProblems, defaultTicks, statusLabel, chartPaths, staleness,
  chartDays, seriesMax, compareText, problemAction, lastRun, runState, jobName, runSummary,
  initialReplay, replayStep, replayBatches, replayCount, previewLabel, rangeError, yesterday, money,
  initialCallReport, callReportStep, resultCounts, mergePreviews, replaySentText, replayFailureText, errorText, applyFailureText,
  DESTINATIONS, DEST_LABELS, MAX_CSV_BYTES
};
